/**
 * Display-name helpers for the appeals surface (AUT-130). Mirrors the
 * reports / payouts versions — kept page-local so the appeals page never
 * imports across sibling vertical boundaries.
 *
 * The server sends `customerName` / `inspectorName` already
 * redacted-or-full per `piiRevealed`. These helpers only derive avatar
 * initials + a short id from values the SPA already holds — they never
 * reconstruct PII. Cyrillic-aware so the initials of "Мария Ковалёва"
 * render as "МК", not "MK".
 */

/** Two-letter avatar initials from a display name; '—' fallback. */
export const initials = (name: string | null): string => {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return '—';
  const second = parts[1];
  if (!second) return first.slice(0, 2).toLocaleUpperCase();
  return ((first[0] ?? '') + (second[0] ?? '')).toLocaleUpperCase();
};

/** Truncate an appeal id to the mockup's `apl_9c81…03e` shape. */
export const shortId = (id: string): string => {
  if (id.length <= 11) return id;
  return `${id.slice(0, 8)}…${id.slice(-3)}`;
};

/**
 * Header avatar tag derived from `appealNumber` — the mockup renders
 * `AP-317` in a square rounded avatar. We return the sanitised display
 * string; if no number is set we fall back to "AP".
 */
export const appealTag = (appealNumber: string | null | undefined): string => {
  if (!appealNumber || appealNumber.length === 0) return 'AP';
  return appealNumber;
};
